import { useMemo } from 'react';
import { BigNum, QUOTE_PRECISION_EXP, ZERO } from '@velocity-exchange/sdk';
import { toNotional } from 'src/utils/formatting';
import useDriftClient from './useDriftClient';
import useDriftClientIsReady from './useDriftClientIsReady';
import useWalletIsConnected from './useWalletIsConnected';
import useUserAccountIsReady from './useUserAccountIsReady';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import {
	isIsolatedPositionBeingLiquidated,
	getHealthWithOverride,
} from 'src/utils/dev/devLiquidationOverrides';
import { useIsolatedPositionMargin } from './positions/useIsolatedPositionMargin';

export const usePositionSummary = (selection: 'cross' | `iso-${number}`) => {
	const connected = useWalletIsConnected();
	const driftClient = useDriftClient();
	const isDriftClientReady = useDriftClientIsReady();
	const userAccountIsReady = useUserAccountIsReady();
	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const userClient =
		isDriftClientReady && userAccountIsReady && driftClient?.hasUser()
			? driftClient.getUser()
			: undefined;

	// Compute margin calculation once per render for maintenance category
	const marginCalc = useMemo(() => {
		return userClient && userAccountIsReady
			? userClient.getMarginCalculation('Maintenance')
			: undefined;
	}, [userClient, userAccountIsReady]);

	const perpMarketIndex = useMemo(() => {
		if (selection.startsWith('iso-')) {
			return Number(selection.split('iso-')[1]);
		}
		return undefined;
	}, [selection]);

	const positionIsolatedMargin = useIsolatedPositionMargin(
		perpMarketIndex ?? 0
	);

	const { healthPercentFormatted, health, totalCollateral, maintenanceMargin } =
		useMemo(() => {
			if (!userClient || !marginCalc) {
				return {
					healthPercentFormatted: '100.0%',
					health: 100,
					totalCollateral: toNotional(0),
					maintenanceMargin: toNotional(0),
					perpMarketIndex: undefined as number | undefined,
				};
			}

			let health = 100;
			let healthPercentFormatted = '100.0%';
			let totalCollBn = ZERO;
			let maintReqBn = ZERO;
			let marketIndex: number | undefined = undefined;

			if (selection.startsWith('iso-')) {
				const idx = Number(selection.split('iso-')[1]);
				marketIndex = idx;
				const iso = marginCalc.isolatedMarginCalculations.get(idx);
				const isIsoBeingLiquidated =
					currentUserKey &&
					isIsolatedPositionBeingLiquidated(userClient, idx, currentUserKey);
				if (isIsoBeingLiquidated) {
					health = 0;
					healthPercentFormatted = 'Liquidating';
				} else {
					if (iso) {
						totalCollBn = iso.totalCollateral;
						maintReqBn = iso.marginRequirement;
					}
					health =
						connected && userAccountIsReady
							? getHealthWithOverride(userClient, idx, currentUserKey)
							: 100;
					healthPercentFormatted = `${health.toFixed(1)}%`;
				}
			} else {
				totalCollBn = marginCalc.totalCollateral;
				maintReqBn = marginCalc.marginRequirement;
				health = connected && userAccountIsReady ? userClient.getHealth() : 100;
				healthPercentFormatted = userClient.isCrossMarginBeingLiquidated()
					? 'Liquidating'
					: `${health.toFixed(1)}%`;
			}

			return {
				healthPercentFormatted,
				health,
				totalCollateral: toNotional(
					BigNum.from(totalCollBn, QUOTE_PRECISION_EXP).toNum()
				),
				maintenanceMargin: toNotional(
					BigNum.from(maintReqBn, QUOTE_PRECISION_EXP).toNum()
				),
				perpMarketIndex: marketIndex,
			};
		}, [
			selection,
			userClient,
			marginCalc,
			connected,
			userAccountIsReady,
			currentUserKey,
			positionIsolatedMargin,
		]);

	return {
		healthPercentFormatted,
		health,
		totalCollateral,
		maintenanceMargin,
		perpMarketIndex,
	};
};
